import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { getDashboardUrl } from './runtime-config';
import { readStoredAuthToken, readStoredOrgId } from './auth.service';

type UnknownMap = Record<string, unknown>;

export interface InvoiceApiItem {
  id: string;
  createdAt: string;
  url: string;
}

interface InvoiceApiItemWithTs extends InvoiceApiItem {
  createdAtTs: number;
}

@Injectable({ providedIn: 'root' })
export class InvoicesApiService {
  private readonly base = `${getDashboardUrl()}/api/webapp/invoices`;

  constructor(private readonly http: HttpClient) {}

  async listInvoices(): Promise<InvoiceApiItem[]> {
    const orgId = readStoredOrgId();
    const payload = await firstValueFrom(
      this.http.get<unknown>(`${this.base}/${encodeURIComponent(orgId)}`, { headers: this.buildHeaders() })
    );
    const rows = this.extractRows(payload);
    return rows
      .map(row => this.toInvoiceItem(row))
      .filter((row): row is InvoiceApiItemWithTs => !!row)
      .sort((a, b) => b.createdAtTs - a.createdAtTs)
      .map(({ createdAtTs, ...item }) => item);
  }

  private buildHeaders(): HttpHeaders {
    const token = readStoredAuthToken();
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return new HttpHeaders(headers);
  }

  private extractRows(payload: unknown): unknown[] {
    if (Array.isArray(payload)) return payload;
    if (payload && typeof payload === 'object') {
      const source = payload as UnknownMap;
      if (Array.isArray(source['data'])) return source['data'];
      if (Array.isArray(source['items'])) return source['items'];
      if (Array.isArray(source['invoices'])) return source['invoices'];
    }
    return [];
  }

  private toInvoiceItem(payload: unknown): InvoiceApiItemWithTs | null {
    if (!payload || typeof payload !== 'object') return null;
    const row = payload as UnknownMap;

    const id = this.readString(row, ['invoiceId', 'id', 'invoiceNumber', 'number']);
    const url = this.readString(row, ['invoiceUrl', 'url', 'downloadUrl', 'pdfUrl']);
    const createdRaw = this.readString(row, ['createdAt', 'createdOn', 'timestamp', 'date']);
    if (!id || !url) return null;
    const createdTs = new Date(createdRaw).getTime();

    return {
      id,
      url,
      createdAt: this.formatCreatedAt(createdRaw),
      createdAtTs: Number.isNaN(createdTs) ? 0 : createdTs,
    };
  }

  private formatCreatedAt(raw: string): string {
    if (!raw) return '-';
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return raw;
    const d = String(date.getUTCDate()).padStart(2, '0');
    const m = String(date.getUTCMonth() + 1).padStart(2, '0');
    const y = String(date.getUTCFullYear()).slice(-2);
    return `${d}/${m}/${y}`;
  }

  private readString(source: UnknownMap, keys: string[]): string {
    for (const key of keys) {
      const value = source[key];
      if (typeof value === 'string' && value.trim()) return value.trim();
      if (typeof value === 'number') return String(value);
    }
    return '';
  }
}
